import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { Loading } from '../index'; // Replace with actual loading component
import { useAuth } from '../../auth';
import { NoPermission } from '../NoPermission';

/**
 * AdminProtectedRoute component that protects routes based on roles
 * Shows NoPermission component for insufficient roles instead of redirecting
 * 
 * @param {Object} props
 * @param {React.Component} props.component - Component to render if authorized
 * @param {string[]} props.requiredRoles - List of roles allowed to access
 * @param {string} props.path - Route path
 * @param {string} props.fallbackPath - Redirect path if not authenticated (default: '/login')
 * @param {Object} rest - Other props
 */
export function AdminProtectedRoute({
  component: Component,
  requiredRoles = [],
  path,
  fallbackPath = '/login',
  ...rest
}) {
  const auth = useAuth();

  const session = auth.sessionV2 || auth.session || auth.user; // adjust as needed
  
  // Check if auth is still loading (no session data available yet)
  const [hasCheckedAuth, setHasCheckedAuth] = React.useState(false);
  
  // Set a flag after a brief delay to allow Firebase to initialize
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setHasCheckedAuth(true);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  // Get user roles directly from session (populated during signin)
  const userRoles = session?.roles || [];

  // Check if user has the required roles
  const hasAccess = Array.isArray(userRoles) && userRoles.some(role =>
    requiredRoles.includes(role.toLowerCase())
  );

  return (
    <Route
      path={path}
      exact
      {...rest}
      render={(props) => {
        // Show loading briefly while checking auth
        if (!hasCheckedAuth) {
          return (
            <div style={{padding: '2rem', textAlign: 'center'}}>
              <Loading />
              <p>Checking authentication...</p>
            </div>
          );
        }

        // If no session, redirect to login with returnTo
        if (!session) {
          return <Redirect to={`${fallbackPath}?returnTo=${encodeURIComponent(props.location.pathname)}`} />;
        }

        // If logged in but no access, show no permission message
        if (!hasAccess) {
          return <NoPermission 
            title="Insufficient Admin Privileges"
            message={`You need one of the following roles to access this page: ${requiredRoles.join(', ')}. Your current roles: ${userRoles.length > 0 ? userRoles.join(', ') : 'none'}.`}
          />;
        }

        // User has access, render the component
        return <Component {...props} />;
      }}
    />
  );
} 